import { LabelDescriptionOnTooltip } from '@aibees/elephant/components/label-description-on-tooltip';
import { useMapColorToChipColor } from '@aibees/elephant/hooks/use-map-color-to-chip-color';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { SearchType } from '@aibees/elephant/types';
import {
  LabelGrouping,
  LabelType,
  LabelVisibility,
} from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import { Chip, TextEllipsis } from '@aibees/shared/components';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Tooltip } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { RecordManagementLabelProps as Props } from './types';

export const RecordManagementLabel = memo(({ className, label, id }: Props) => {
  const notify = useNotify();
  const { color, name, grouping, description } = label;
  const isList = useMemo(() => label.type === LabelType.LIST, [label.type]);
  const notificationIcon = useMemo(
    () =>
      isList ? notificationIconMap.RemoveList : notificationIconMap.RemoveLabel,
    [isList]
  );
  const isPeople = grouping === LabelGrouping.PEOPLE;
  const api = useSaveRecord(isPeople ? SearchType.People : SearchType.Company);
  const chipColor = useMapColorToChipColor(label);

  const handleRemove = useCallback(() => {
    const key = Date.now().toString();
    if (id)
      api
        .mutateAsync({
          ids: [id],
          kind: 'REMOVE',
          labels: [label.id],
        })
        .then(() => {
          notify.complete(
            `${isList ? 'List' : 'Label'} Deleted Successfully`,
            `The selected ${
              isList ? 'List' : 'Label'
            } has been permanently removed from your system.`,
            key,
            {
              icon: notificationIcon ?? undefined,
            }
          );
        });
  }, [id, label.id, api, notify, isList, notificationIcon]);
  return (
    <Tooltip
      title={description && <LabelDescriptionOnTooltip label={label} />}
      placement="right"
    >
      <Styled.Wrapper
        className={classnames('RecordManagementLabel', className)}
        sx={SX.root({ isList, color })}
      >
        <Chip
          label={<TextEllipsis value={name} w="150px" variant="body4" />}
          startIcon={grouping === LabelGrouping.COMPANY ? 'business' : 'people'}
          endIcon={
            label.visibility === LabelVisibility.PRIVATE
              ? 'eye-lock-outline'
              : undefined
          }
          color={chipColor}
          size="small"
          variant="filled"
          onDelete={handleRemove}
        />
      </Styled.Wrapper>
    </Tooltip>
  );
});

RecordManagementLabel.displayName = 'RecordManagementLabel';
